import { Delete, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'

import {
  AddCommentRequest,
  EditCommentRequest,
  ProviderTaskResponse,
} from '../../../providers/ProviderTask/ProviderTask.provider'
import { ProviderTaskComment } from '../../../providers/ProviderTask/ProviderTask.provider'
import { themeColors } from '../../../theme'

interface TaskCommentProps {
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  myProviderId: number
  commentTask: ProviderTaskResponse | null
  setCommentTask: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse | null>
  >
  newComment: string
  setNewComment: React.Dispatch<React.SetStateAction<string>>
  setOptimisticTasks: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse[]>
  >
  onAddProviderTaskComment: (request: AddCommentRequest) => Promise<any>
  onEditProviderTaskComment: (request: EditCommentRequest) => Promise<any>
  onDeleteProviderTaskComment: (providerTaskCommentId: number) => Promise<any>
}

export const TaskComment = ({
  loading,
  setLoading,
  myProviderId,
  commentTask,
  setCommentTask,
  newComment,
  setNewComment,
  setOptimisticTasks,
  onAddProviderTaskComment,
  onEditProviderTaskComment,
  onDeleteProviderTaskComment,
}: TaskCommentProps) => {
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null)
  const [editCommentText, setEditCommentText] = useState('')

  const startEditing = (commentId: number, currentText: string) => {
    setNewComment('')
    setEditingCommentId(commentId)
    setEditCommentText(currentText)
  }

  const cancelEditing = () => {
    setEditingCommentId(null)
    setEditCommentText('')
  }

  const submitEdit = async (commentId: number) => {
    if (!editCommentText.trim()) return
    await handleEditComment({
      providerTaskCommentId: commentId,
      comment: editCommentText,
    })
    setEditingCommentId(null)
    setEditCommentText('')
  }

  const confirmDelete = async (commentId: number) => {
    if (!window.confirm('Delete this comment?')) return
    await handleDeleteComment(commentId)
  }

  const handleAddComment = async (request: AddCommentRequest) => {
    if (!newComment.trim()) return

    try {
      setLoading(true)
      const created = await onAddProviderTaskComment(request)

      if (commentTask) {
        setCommentTask({
          ...commentTask,
          providerTaskComments: [
            {
              id: created.id,
              providerTaskId: commentTask.id,
              comment: newComment,
              createdByProviderId: myProviderId,
              createdByProviderName: created.createdByProviderName,
              createdAt: created.createdAt,
            } as ProviderTaskComment,
            ...commentTask.providerTaskComments,
          ],
        })
      }

      setNewComment('')
      setLoading(false)
    } catch (error) {
      console.log('error', error)
      // Show error, comment stays in the input so they can retry
      alert('Failed to post comment. Please try again.')

      setLoading(false)
    }
  }

  const handleEditComment = async (request: EditCommentRequest) => {
    if (!commentTask) return

    setLoading(true)

    const previousComments = commentTask.providerTaskComments
    const updatedComments = previousComments.map((c) =>
      c.id === request.providerTaskCommentId
        ? { ...c, comment: request.comment }
        : c
    )

    setCommentTask({ ...commentTask, providerTaskComments: updatedComments })
    setOptimisticTasks((prev) =>
      prev.map((t) =>
        t.id === commentTask.id
          ? { ...t, providerTaskComments: updatedComments }
          : t
      )
    )

    try {
      await onEditProviderTaskComment(request)
    } catch (error) {
      console.log('error', error)
      setCommentTask({ ...commentTask, providerTaskComments: previousComments })
      setOptimisticTasks((prev) =>
        prev.map((t) =>
          t.id === commentTask.id
            ? { ...t, providerTaskComments: previousComments }
            : t
        )
      )
      alert('Failed to edit comment. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteComment = async (commentId: number) => {
    if (!commentTask) return

    const previousComments = commentTask.providerTaskComments
    const updatedComments = previousComments.filter((c) => c.id !== commentId)

    setCommentTask({ ...commentTask, providerTaskComments: updatedComments })
    setOptimisticTasks((prev) =>
      prev.map((t) =>
        t.id === commentTask.id
          ? { ...t, providerTaskComments: updatedComments }
          : t
      )
    )

    try {
      await onDeleteProviderTaskComment(commentId)
    } catch (error) {
      console.log('error', error)
      setCommentTask({ ...commentTask, providerTaskComments: previousComments })
      setOptimisticTasks((prev) =>
        prev.map((t) =>
          t.id === commentTask.id
            ? { ...t, providerTaskComments: previousComments }
            : t
        )
      )
      alert('Failed to delete comment. Please try again.')
    }
  }

  return (
    <Dialog
      open={!!commentTask}
      onClose={() => {
        setCommentTask(null)
        setNewComment('')
      }}
      fullWidth
      maxWidth='sm'
      disableAutoFocus
    >
      <DialogTitle>Comments — {commentTask?.providerTaskType.type}</DialogTitle>

      <DialogContent>
        {/* New Comment Input */}
        <TextField
          fullWidth
          multiline
          label='Write a comment'
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          sx={{ mt: 1, mb: 1, fontSize: 15, height: 180 }}
        />

        <DialogActions>
          <Button
            onClick={() => {
              setCommentTask(null)
              setNewComment('')
            }}
          >
            Close
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              commentTask &&
                handleAddComment({
                  providerTaskId: commentTask.id,
                  comment: newComment,
                })
            }}
            disabled={!newComment.trim() || loading}
            sx={{ backgroundColor: themeColors.darkPurple }}
          >
            {loading && newComment ? (
              <CircularProgress size={20} color='primary' />
            ) : (
              'Post Comment'
            )}
          </Button>
        </DialogActions>

        {/* Existing Comments */}
        {!commentTask?.providerTaskComments?.length ? (
          <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
            No comments yet.
          </Typography>
        ) : (
          commentTask.providerTaskComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              newComment={newComment}
              myProviderId={myProviderId}
              loading={loading}
              isEditing={editingCommentId === comment.id}
              editCommentText={editCommentText}
              setEditCommentText={setEditCommentText}
              onStartEditing={startEditing}
              onCancelEditing={cancelEditing}
              onSubmitEdit={submitEdit}
              onDelete={confirmDelete}
            />
          ))
        )}
      </DialogContent>
    </Dialog>
  )
}

// component for each comment
interface CommentItemProps {
  comment: ProviderTaskComment
  newComment: string
  myProviderId: number
  loading: boolean
  isEditing: boolean
  editCommentText: string
  setEditCommentText: (text: string) => void
  onStartEditing: (commentId: number, currentText: string) => void
  onCancelEditing: () => void
  onSubmitEdit: (commentId: number) => void
  onDelete: (commentId: number) => void
}

export const CommentItem = ({
  comment,
  newComment,
  myProviderId,
  loading,
  isEditing,
  editCommentText,
  setEditCommentText,
  onStartEditing,
  onCancelEditing,
  onSubmitEdit,
  onDelete,
}: CommentItemProps) => {
  const isOwnComment = comment.createdByProviderId === myProviderId

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        marginY: 2,
        padding: 2,
        backgroundColor: themeColors.grayWhite,
        borderRadius: 2,
        border: `1px solid ${themeColors.darkPurple}`,
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      {isEditing && !newComment ? (
        <TextField
          fullWidth
          multiline
          variant='standard'
          value={editCommentText}
          onChange={(e) => setEditCommentText(e.target.value)}
          InputProps={{ disableUnderline: true }}
          sx={{
            overflow: 'scroll',
            '& .MuiInputBase-root': {
              width: '100%',
              padding: 0,
              fontSize: '0.875rem', // matches Typography variant='body2'
              lineHeight: 21,
              backgroundColor: 'none',
            },
            '& .MuiInputBase-input': {
              padding: 0,
              backgroundColor: themeColors.grayWhite,
            },
            fontSize: 15,
            height: 100,
          }}
        />
      ) : (
        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
          {comment.comment}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 1,
        }}
      >
        <Typography variant='caption' color='textSecondary'>
          {comment.createdByProviderName}
        </Typography>

        <Typography
          variant='caption'
          color='textSecondary'
          sx={{ marginRight: 'auto' }}
        >
          {dayjs(comment.createdAt).format('MM/DD/YYYY h:mm A')}
        </Typography>

        {isOwnComment && (
          <>
            {isEditing && !newComment ? (
              <Button size='small' onClick={onCancelEditing}>
                Cancel
              </Button>
            ) : (
              <Tooltip title='Edit'>
                <IconButton
                  size='small'
                  onClick={() => onStartEditing(comment.id, comment.comment)}
                >
                  <Edit fontSize='small' color='primary' />
                </IconButton>
              </Tooltip>
            )}

            {isEditing && !newComment ? (
              <Button
                size='small'
                variant='contained'
                onClick={() => onSubmitEdit(comment.id)}
                disabled={!editCommentText.trim() || loading}
                sx={{ backgroundColor: themeColors.darkPurple }}
              >
                {loading ? (
                  <CircularProgress size={20} color='primary' />
                ) : (
                  'Save Changes'
                )}
              </Button>
            ) : (
              <Tooltip title='Delete'>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => onDelete(comment.id)}
                >
                  <Delete fontSize='small' color='error' />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
